import {
  activePlayer,
  canAttackBoss,
  fortifyTargets,
  getAttackTargets,
  lairAdjacent,
  movableTroops,
  neighborsOf,
  ownedTerritories,
  placeableTerritories,
  powerTargets,
  canUseHarmony,
  validTrades,
} from './selectors';
import type { Action, LootKind, PlayerId, RulesView } from './types';
import { checkAction, stealVictims } from './validate';

const range = (a: number, b: number) => {
  const out: number[] = [];
  for (let i = a; i <= b; i++) out.push(i);
  return out;
};

/**
 * Todas as ações legais de `player` agora. Funciona com GameState ou PlayerView
 * (a view do próprio jogador tem tudo o que é preciso).
 */
export function getLegalActions(s: RulesView, player: PlayerId): Action[] {
  if (activePlayer(s) !== player) return [];
  const out: Action[] = [];
  const push = (a: Action) => {
    if (checkAction(s, a) === null) out.push(a);
  };
  const pending = s.pending;
  if (pending) {
    switch (pending.kind) {
      case 'conquest':
        for (const count of range(pending.min, pending.max)) push({ type: 'moveIn', player, count });
        break;
      case 'lootChoice':
        for (const kind of new Set(pending.options)) push({ type: 'chooseLoot', player, kind });
        break;
      case 'bossRegion':
        for (const region of pending.regions) push({ type: 'chooseBossRegion', player, region });
        break;
      case 'reaction':
        push({ type: 'react', player, use: true });
        push({ type: 'react', player, use: false });
        break;
      case 'lootOverflow':
        for (const kind of new Set(s.players[player].loot ?? [])) push({ type: 'discardLoot', player, kind });
        pushLoot(s, player, push);
        break;
      case 'heal':
        for (const territory of ownedTerritories(s, player)) push({ type: 'heal', player, territory });
        break;
    }
    return out;
  }

  const owned = ownedTerritories(s, player);
  if (s.phase === 'setup') {
    const setup = s.setup!;
    const max = Math.min(s.info.ruleset.setupBatch - setup.placedThisGo, setup.remaining[player]);
    for (const territory of owned)
      for (const count of range(1, max)) push({ type: 'place', player, territory, count });
    push({ type: 'autoSetup', player });
    return out;
  }

  const me = s.players[player];
  if (s.phase === 'reinforce') {
    for (const cards of validTrades(s, me.cards ?? [])) push({ type: 'trade', player, cards });
  }
  if (s.reinforcements > 0) {
    for (const territory of placeableTerritories(s, player))
      for (const count of range(1, s.reinforcements)) push({ type: 'place', player, territory, count });
  }
  push({ type: 'endPhase', player });

  if (s.phase === 'attack') {
    for (const from of owned) {
      for (const to of getAttackTargets(s, player, from)) {
        push({ type: 'attack', player, from, to });
        push({ type: 'attack', player, from, to, untilEnd: true });
      }
    }
    if (s.boss) {
      for (const from of lairAdjacent(s, s.boss.region))
        if (canAttackBoss(s, player, from)) push({ type: 'attackBoss', player, from });
    }
    for (const target of powerTargets(s, player)) push({ type: 'power', player, target });
    if (canUseHarmony(s, player)) push({ type: 'harmony', player });
  }

  if (s.phase === 'fortify') {
    for (const from of owned) {
      const max = movableTroops(s, from);
      if (max < 1) continue;
      for (const to of fortifyTargets(s, player, from))
        for (const count of range(1, max)) push({ type: 'fortify', player, from, to, count });
    }
  }

  pushLoot(s, player, push);
  return out;
}

/** Combinações de até `k` itens (todas as não vazias). */
function subsets<T>(items: T[], k: number): T[][] {
  const out: T[][] = [];
  const rec = (start: number, acc: T[]) => {
    if (acc.length) out.push(acc);
    if (acc.length === k) return;
    for (let i = start; i < items.length; i++) rec(i + 1, [...acc, items[i]]);
  };
  rec(0, []);
  return out;
}

function pushLoot(s: RulesView, player: PlayerId, push: (a: Action) => void): void {
  const owned = ownedTerritories(s, player);
  const enemies = Object.keys(s.territories).filter((t) => s.territories[t].owner !== player);
  for (const kind of new Set<LootKind>(s.players[player].loot ?? [])) {
    switch (kind) {
      case 'blessing':
      case 'ward':
      case 'restore':
        for (const territory of owned) push({ type: 'playLoot', player, kind, territory });
        break;
      case 'strike':
      case 'paralyze':
        for (const territory of enemies) if (s.territories[territory].troops >= 2) push({ type: 'playLoot', player, kind, territory });
        break;
      case 'barrage':
        // Só alvos com 2+ tropas (com 1 a runa não tira nada); a validação aceita qualquer inimigo vizinho.
        for (const territory of owned) {
          const near = neighborsOf(s, territory).filter((n) => s.territories[n].owner !== player && s.territories[n].troops >= 2);
          for (const targets of subsets(near, s.info.ruleset.barrageTargets)) push({ type: 'playLoot', player, kind, territory, targets });
        }
        break;
      case 'wall':
        for (const l of s.info.content.map.links) push({ type: 'playLoot', player, kind, from: l.a, to: l.b });
        break;
      case 'teleport':
        for (const from of owned) {
          const max = movableTroops(s, from);
          if (max < 1) continue;
          for (const to of owned) {
            if (to === from) continue;
            for (const count of range(1, max)) push({ type: 'playLoot', player, kind, from, to, count });
          }
        }
        break;
      case 'steal':
        for (const victim of stealVictims(s, player)) push({ type: 'playLoot', player, kind, victim });
        break;
      default:
        push({ type: 'playLoot', player, kind });
    }
  }
}

export function isLegal(s: RulesView, a: Action): boolean {
  return checkAction(s, a) === null;
}
